/**
 * useAgentPipelineState — derives React Flow node/edge state from WebSocket progress events.
 *
 * Consumes the latest `progress_update` event and maps aggregate progress
 * to per-node states for the 4-node pipeline:
 *   Graph Engine → Document Researcher → Evidence Compiler → Citation Tracker
 *
 * Display smoothing: nodes are held in 'active' state for a minimum of
 * MIN_ACTIVE_MS before transitioning to 'complete', so fast-completing
 * agents (Evidence Compiler, Citation Tracker) are visible to the viewer.
 */

import { useRef, useMemo, useState, useEffect } from 'react';

// Minimum time a node stays visually 'active' before showing 'complete' (ms)
const MIN_ACTIVE_MS = 1500;

const NODES_CONFIG = [
  { id: 'graph_engine',        label: 'Graph Engine',        icon: '🔀' },
  { id: 'document_researcher', label: 'Document Researcher', icon: '🔍' },
  { id: 'evidence_compiler',   label: 'Evidence Compiler',   icon: '📊' },
  { id: 'citation_tracker',    label: 'Citation Tracker',    icon: '📝' },
];

const EDGES_CONFIG = [
  { id: 'e1', source: 'graph_engine',        target: 'document_researcher' },
  { id: 'e2', source: 'document_researcher', target: 'evidence_compiler'   },
  { id: 'e3', source: 'evidence_compiler',   target: 'citation_tracker'    },
];

const NODE_POSITIONS = {
  graph_engine:        { x: 10,  y: 60 },
  document_researcher: { x: 190, y: 60 },
  evidence_compiler:   { x: 370, y: 60 },
  citation_tracker:    { x: 550, y: 60 },
};

function deriveRawStatus(nodeId, pct, isComplete, hasStarted) {
  if (isComplete || pct >= 100) return 'complete';
  if (!hasStarted) return 'pending';
  switch (nodeId) {
    case 'graph_engine':        return 'active';  // active as soon as processing starts
    case 'document_researcher': return pct <= 40 ? 'active' : 'complete';
    case 'evidence_compiler':   return pct <= 40 ? 'pending' : pct <= 70 ? 'active' : 'complete';
    case 'citation_tracker':    return pct <= 70 ? 'pending' : 'active';
    default:                    return 'pending';
  }
}

function cleanActivity(rawActivity, currentStage) {
  if (!rawActivity) return '';
  if (currentStage === 'kb_retrieval' || rawActivity.includes('document chunks via Knowledge Base')) {
    const chunkMatch = rawActivity.match(/(\d+)\s+document/i);
    return chunkMatch ? `Searching KB — ${chunkMatch[1]} chunks` : 'Searching Knowledge Base...';
  }
  return rawActivity.slice(0, 55);
}

export default function useAgentPipelineState(progressData, processingComplete, isProcessing) {
  const agentStartTimes   = useRef({});
  const agentElapsedTimes = useRef({});
  const activeStartTimes  = useRef({});   // when node first became 'active'
  const pendingComplete   = useRef({});   // nodes waiting for MIN_ACTIVE_MS before going complete

  // Tick counter to force re-render when timers expire
  const [tick, setTick] = useState(0);

  const pct             = progressData?.progress_percentage ?? 0;
  const completedQ      = progressData?.completed_questions ?? 0;
  const totalQ          = progressData?.total_questions     ?? 6;
  const currentQuestion = progressData?.current_question    ?? '';
  const currentStage    = progressData?.current_stage       ?? '';
  const isComplete      = processingComplete || pct >= 100;
  const hasStarted      = isProcessing || pct > 0 || isComplete;

  // Schedule a re-render after MIN_ACTIVE_MS for any node that just hit 'complete'
  useEffect(() => {
    const now = Date.now();
    let earliestExpiry = null;

    for (const nodeId of Object.keys(pendingComplete.current)) {
      const readyAt = pendingComplete.current[nodeId];
      if (readyAt > now) {
        if (earliestExpiry === null || readyAt < earliestExpiry) {
          earliestExpiry = readyAt;
        }
      }
    }

    if (earliestExpiry !== null) {
      const delay = earliestExpiry - now;
      const t = setTimeout(() => setTick(n => n + 1), delay + 10);
      return () => clearTimeout(t);
    }
  }, [pct, isComplete, tick]);

  const nodes = useMemo(() => {
    const now = Date.now();

    return NODES_CONFIG.map(node => {
      const rawStatus = deriveRawStatus(node.id, pct, isComplete, hasStarted);

      // --- Display smoothing ---
      // When a node first becomes 'active', record the time
      if (rawStatus === 'active' && !activeStartTimes.current[node.id]) {
        activeStartTimes.current[node.id] = now;
      }

      // When raw status becomes 'complete', schedule the visual transition
      if (rawStatus === 'complete' && activeStartTimes.current[node.id]) {
        const activeSince = activeStartTimes.current[node.id];
        const readyAt = activeSince + MIN_ACTIVE_MS;
        if (!pendingComplete.current[node.id]) {
          pendingComplete.current[node.id] = readyAt;
        }
      }

      // Determine display status — hold as 'active' until MIN_ACTIVE_MS has elapsed
      let displayStatus = rawStatus;
      if (rawStatus === 'complete' && pendingComplete.current[node.id]) {
        const readyAt = pendingComplete.current[node.id];
        if (now < readyAt) {
          displayStatus = 'active'; // still showing active
        }
      }

      // Elapsed time tracking (based on raw transitions, not display)
      if (rawStatus !== 'active' && agentStartTimes.current[node.id] && !agentElapsedTimes.current[node.id]) {
        agentElapsedTimes.current[node.id] =
          (now - agentStartTimes.current[node.id]) / 1000;
      }
      if (rawStatus === 'active' && !agentStartTimes.current[node.id]) {
        agentStartTimes.current[node.id] = now;
      }

      const rawActivity = displayStatus === 'active' ? currentQuestion : '';
      const activity    = cleanActivity(rawActivity, currentStage);

      const subLabel = node.id === 'graph_engine'
        ? (hasStarted ? `${totalQ} / ${totalQ} running in parallel` : 'Ready')
        : activity;

      const elapsedTime = agentElapsedTimes.current[node.id] || null;

      return {
        id:          node.id,
        type:        'agentNode',
        position:    NODE_POSITIONS[node.id],
        draggable:   false,
        connectable: false,
        selectable:  false,
        data: {
          label:           node.label,
          icon:            node.icon,
          status:          displayStatus,
          completedCount:  displayStatus === 'complete' ? totalQ : (node.id === 'graph_engine' ? totalQ : completedQ),
          totalCount:      totalQ,
          currentActivity: subLabel,
          elapsedTime:     displayStatus === 'complete' ? elapsedTime : null,
        },
      };
    });
  }, [pct, completedQ, totalQ, currentQuestion, currentStage, isComplete, hasStarted, tick]);

  const edges = useMemo(() => {
    return EDGES_CONFIG.map(edge => {
      const targetNode   = nodes.find(n => n.id === edge.target);
      const targetStatus = targetNode?.data?.status || 'pending';
      return {
        id:       edge.id,
        source:   edge.source,
        target:   edge.target,
        type:     'animatedEdge',
        data:     { status: targetStatus },
        animated: targetStatus === 'active',
      };
    });
  }, [nodes]);

  const announcement = useMemo(() => {
    if (isComplete) return 'Research pipeline complete';
    const active = nodes.find(n => n.data.status === 'active' && n.id !== 'graph_engine');
    return active ? `${active.data.label} is now active` : '';
  }, [nodes, isComplete]);

  return { nodes, edges, announcement };
}
